import { cvaMerge, type VariantProps } from '@payaca/utilities/cvaMerge';
import { HTMLMotionProps, motion } from 'motion/react';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const pageBannerVariants = cvaMerge(
  'flex items-center justify-center space-x-1.5 p-3 border-b',
  {
    variants: {
      variant: {
        primary: 'bg-purple-300',
        secondary: 'bg-white',
        success: 'bg-green-300',
        warning: 'bg-orange-300',
        error: 'bg-red-300',
      },
    },
    defaultVariants: { variant: 'primary' },
  }
);

export type BannerBarProps = HTMLMotionProps<'div'> &
  VariantProps<typeof pageBannerVariants>;

/**
 * A banner strip, with no opinion about where on the page it renders. Reach for
 * this where a banner belongs to one region of a page; use {@link PageBanner}
 * for the app-wide strip above every page.
 *
 * Carries no `role` of its own, since a banner offering an action is not a
 * status message. Pass one where the content warrants it.
 */
export const BannerBar: FC<BannerBarProps> = (props) => {
  const { children, className, variant, ...rest } = props;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={pageBannerVariants({ variant, className })}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

/**
 * A banner in the app's own banner strip, above the page content and the chat
 * agent sidebar alike. Renders through a portal, so it appears there wherever
 * in the tree it is written.
 */
export const PageBanner: FC<BannerBarProps> = (props) => {
  return (
    <PageBannerPortal>
      <BannerBar role="status" {...props} />
    </PageBannerPortal>
  );
};

const PageBannerPortal: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  const [target, setTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setTarget(document.getElementById('banner-portal'));
  }, []);

  if (!target) return null;
  return createPortal(<>{children}</>, target);
};

export default PageBanner;
